/**
 * LeftShield Portal - Enterprise Governance Graph Page
 * Explore relationships between findings, controls, frameworks, and resources.
 * Route: /graph | Category: AI
 *
 * Node list migrated onto the List & Grid Standard — see
 * `.github/context/list-grid-standard.context.md` and
 * `.github/context/list-grid-standard-behaviours.context.md`.
 * Read-only list (no mutations) — Actions column carries a chevron-only affordance
 * that opens the existing context Drawer; no role gating is required (view-only).
 */
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import {
  makeStyles,
  tokens,
  Card,
  CardHeader,
  Body1,
  Caption1,
  Badge,
  Button,
  Spinner,
  TabList,
  Tab,
  Drawer,
  DrawerHeader,
  DrawerHeaderTitle,
  DrawerBody,
  Divider,
  Title2,
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableHeaderCell,
  TableRow,
} from '@fluentui/react-components';
import {
  ShareAndroidRegular,
  DismissRegular,
  WarningRegular,
  QuestionCircleRegular,
  ChevronRight24Regular,
  ArrowSort16Regular,
  ArrowSortUp16Regular,
  ArrowSortDown16Regular,
} from '@fluentui/react-icons';
import { usePageStyles } from '../styles/page-styles';
import { PageHeader } from '../components/common/PageHeader';
import { ekgApi } from '../services/api';
import type { EkgNode, EkgNodeType, EkgEdge, EkgContext } from '../types';
import {
  ListPageLayout,
  ListToolbar,
  Pager,
  actionsColumnWidth,
  useListUrlState,
  useAdaptiveGridData,
  LoadingSkeleton,
  ErrorState,
  EmptyState,
  ListLoadingOverlay,
} from '../components/list';
import type { ListQueryParams, PagedResult, SortDirection, SortState } from '../components/list';
import { semanticColors, solutionTokens } from '../theme/solution-theme';

const NODE_TYPE_COLORS: Record<EkgNodeType, string> = {
  Policy: tokens.colorBrandBackground,
  Control: tokens.colorBrandBackground,
  Requirement: tokens.colorBrandBackground,
  Evidence: tokens.colorBrandBackground,
  Finding: semanticColors.error,
  Risk: semanticColors.error,
  Incident: semanticColors.error,
  Asset: semanticColors.warning,
};

const EDGE_TYPE_LABELS: Record<string, string> = {
  PolicyImplementsControl: 'Implements',
  ControlSatisfiesRequirement: 'Satisfies',
  EvidenceSupportsControl: 'Supports',
  FindingViolatesControl: 'Violates',
  AssetSubjectToPolicy: 'Subject To',
  RiskAffectsAsset: 'Affects',
  IncidentExploitsRisk: 'Exploits',
  FindingAffectsAsset: 'Affects',
  EvidenceFromEvaluation: 'From Evaluation',
};

const ALL_NODE_TYPES: EkgNodeType[] = [
  'Policy', 'Control', 'Requirement', 'Asset', 'Risk', 'Incident', 'Evidence', 'Finding',
];

const useStyles = makeStyles({
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))',
    gap: tokens.spacingHorizontalL,
  },
  statCard: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
    textAlign: 'center' as const,
    padding: tokens.spacingVerticalL,
    borderRadius: tokens.borderRadiusMedium,
    backgroundColor: tokens.colorNeutralBackground2,
  },
  statValue: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightBold,
    lineHeight: tokens.lineHeightHero800,
  },
  listSection: {
    flexGrow: 1,
    minHeight: 0,
    display: 'flex',
    flexDirection: 'column',
  },
  nodeTypeTabs: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalM,
    flexWrap: 'wrap',
  },
  drawerContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
  },
  neighborSection: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalS,
  },
  neighborItem: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    padding: tokens.spacingVerticalXS,
    borderRadius: tokens.borderRadiusSmall,
    backgroundColor: tokens.colorNeutralBackground2,
  },
});

/** Wraps the server-paged `ekgApi.getNodes` (skip/take) as a `PagedResult` for `useAdaptiveGridData`. */
async function fetchEkgNodesPage(
  nodeTypeFilter: string,
  params: ListQueryParams
): Promise<PagedResult<EkgNode>> {
  const skip = (params.page - 1) * params.pageSize;
  const result = await ekgApi.getNodes(nodeTypeFilter || undefined, skip, params.pageSize);
  const totalPages = Math.max(Math.ceil(result.totalCount / params.pageSize), 1);
  return {
    items: result.items,
    totalCount: result.totalCount,
    pageNumber: params.page,
    pageSize: params.pageSize,
    totalPages,
    hasPreviousPage: params.page > 1,
    hasNextPage: params.page < totalPages,
  };
}

export const GraphPage: React.FC = () => {
  const ps = usePageStyles();
  const styles = useStyles();
  const queryClient = useQueryClient();

  const [selectedNode, setSelectedNode] = useState<EkgNode | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [showGaps, setShowGaps] = useState(false);

  const { state, setPage, setSort, setSearch, setFilters } = useListUrlState({});

  // Node-type tab selection is encoded into the shared `filters` URL param (empty = "All").
  const nodeTypeFilter = state.filters;
  const selectedNodeType = nodeTypeFilter === '' ? 'all' : nodeTypeFilter;
  const handleNodeTypeSelect = (value: string) => setFilters(value === 'all' ? '' : value);

  const [sortField, sortDirRaw] = state.sort ? state.sort.split(':') : [undefined, undefined];
  const sortDir: SortDirection = sortDirRaw === 'desc' ? 'desc' : 'asc';
  const sortState: SortState[] = sortField ? [{ field: sortField, dir: sortDir }] : [];

  const toggleSort = (field: string) => {
    if (field === sortField) {
      setSort(`${field}:${sortDir === 'asc' ? 'desc' : 'asc'}`);
    } else {
      setSort(`${field}:asc`);
    }
  };

  const sortIcon = (field: string) => {
    if (field !== sortField) return <ArrowSort16Regular />;
    return sortDir === 'asc' ? <ArrowSortUp16Regular /> : <ArrowSortDown16Regular />;
  };

  const { data: stats, isLoading: statsLoading } = useQuery({
    queryKey: ['ekg-stats'],
    queryFn: () => ekgApi.getStats(),
  });

  const grid = useAdaptiveGridData<EkgNode>({
    queryKeyBase: ['ekg-nodes', nodeTypeFilter],
    fetchPage: (params) => fetchEkgNodesPage(nodeTypeFilter, params),
    page: state.page,
    pageSize: state.pageSize,
    search: state.search,
    searchFields: ['label', 'nodeType', 'externalId'],
    sortState,
  });

  const {
    items: nodeItems,
    totalCount,
    totalPages,
    hasPreviousPage,
    hasNextPage,
    isLoading: nodesLoading,
    isFetching: nodesFetching,
    isError: nodesError,
    refetch: refetchNodes,
  } = grid;

  // Out-of-range clamp — never strand the user on an empty page after a filter/search change.
  useEffect(() => {
    if (!nodesLoading && state.page > totalPages) {
      setPage(totalPages);
    }
  }, [nodesLoading, state.page, totalPages, setPage]);

  // Gap Analysis reads an independent (unpaginated, up-to-200) fetch for the current node-type
  // tab — decoupled from the paginated table grid — matching the original panel's data source.
  // Lazily enabled (only while the panel is open) since it's a separate network call now.
  const { data: gapNodeList } = useQuery({
    queryKey: ['ekg-nodes-gap-scan', nodeTypeFilter],
    queryFn: () => ekgApi.getNodes(nodeTypeFilter || undefined, 0, 200),
    enabled: showGaps,
  });

  const { data: context } = useQuery({
    queryKey: ['ekg-context', selectedNode?.id],
    queryFn: () => ekgApi.getContext(selectedNode!.id, 2),
    enabled: !!selectedNode,
  });

  // §12746/#12750 — the open node is reflected in the URL as `?node={referenceId}`, making the
  // context view a shareable deep link and keeping it stable across refresh/back-forward. The
  // `node` param is written/cleared with a functional updater that preserves the list-state
  // params owned by `useListUrlState` (page/sort/search/filters). A separate `useSearchParams`
  // binding is safe — react-router shares one URL, and both writers merge onto `prev`.
  const [searchParams, setSearchParams] = useSearchParams();
  const nodeParam = searchParams.get('node');

  const setNodeParam = useCallback(
    (referenceId: string | null) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          if (referenceId) next.set('node', referenceId);
          else next.delete('node');
          return next;
        },
        { replace: false },
      );
    },
    [setSearchParams],
  );

  const openNode = useCallback(
    (node: EkgNode) => {
      setSelectedNode(node);
      setDrawerOpen(true);
      setNodeParam(node.referenceId);
    },
    [setNodeParam],
  );

  const closeDrawer = useCallback(() => {
    setDrawerOpen(false);
    setSelectedNode(null);
    setNodeParam(null);
  }, [setNodeParam]);

  const handleNodeClick = openNode;

  // §12773 — cold deep-link fallback. The fast path below resolves `?node={referenceId}`
  // against the currently-loaded page of nodes; when it's NOT found there (a node deep-linked
  // from another page/context), fetch its context by reference id directly instead of
  // silently no-op'ing. Enabled only while there's no in-page match AND `selectedNode` hasn't
  // already been resolved to this referenceId — once the effect below sets `selectedNode`,
  // this condition goes false and the query stops (no loop, no re-fetch on every render).
  const inPageMatch = nodeParam ? nodeItems.find((n) => n.referenceId === nodeParam) : undefined;
  const needsColdLookup = !!nodeParam && !inPageMatch && selectedNode?.referenceId !== nodeParam;

  const { data: coldContext } = useQuery({
    queryKey: ['ekg-context-by-reference', nodeParam],
    queryFn: () => ekgApi.getContextByReferenceId(nodeParam!),
    enabled: needsColdLookup,
  });

  // Deep-link resolution: on landing with `?node={referenceId}` (or a back-forward change),
  // resolve the referenceId against the loaded page of nodes and open its context (fast path).
  // If not found there, fall back to the cold-lookup context fetched above, keyed by
  // referenceId. Harmless no-op while `coldContext` hasn't resolved yet (e.g. still loading,
  // or the referenceId 404s because it doesn't exist).
  useEffect(() => {
    if (!nodeParam) {
      if (drawerOpen) {
        setDrawerOpen(false);
        setSelectedNode(null);
      }
      return;
    }
    if (selectedNode?.referenceId === nodeParam) return;
    const match = nodeItems.find((n) => n.referenceId === nodeParam);
    if (match) {
      setSelectedNode(match);
      setDrawerOpen(true);
      return;
    }
    if (coldContext?.centerNode) {
      // Seed the numeric-id-keyed context query (below) with the by-reference result so the
      // drawer's existing `['ekg-context', selectedNode?.id]` query serves from cache instead
      // of an immediate, redundant duplicate network round-trip for data already in hand.
      queryClient.setQueryData(['ekg-context', coldContext.centerNode.id], coldContext);
      setSelectedNode(coldContext.centerNode);
      setDrawerOpen(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [nodeParam, nodeItems, coldContext, queryClient]);

  // Full-page spinner only for the very first load (no stats and no nodes yet) — avoids a
  // layout flash; subsequent loads (filter/page change) use the internal LoadingSkeleton.
  if ((statsLoading || nodesLoading) && !stats && nodesLoading) {
    return (
      <div className={ps.page}>
        <div style={{ display: 'flex', justifyContent: 'center', padding: `calc(${tokens.spacingVerticalXXXL} + ${tokens.spacingVerticalL})` }}>
          <Spinner label="Loading knowledge graph..." />
        </div>
      </div>
    );
  }

  let listBody: ReactNode;
  if (nodesLoading) {
    listBody = <LoadingSkeleton rows={6} columns={4} />;
  } else if (nodesError) {
    listBody = <ErrorState message="Unable to load the governance graph nodes." onRetry={refetchNodes} />;
  } else if (nodeItems.length === 0) {
    listBody = (
      <EmptyState
        icon={<ShareAndroidRegular />}
        title="Governance Graph Empty"
        description="The graph will populate as policies, controls, and evidence are connected. Nodes are created automatically when enforcement evaluations run."
        actionLabel="Connect Delivery Systems"
        onAction={() => { window.location.href = '/developer-tools'; }}
      />
    );
  } else {
    listBody = (
      <ListLoadingOverlay busy={nodesFetching && !nodesLoading}>
        <Table aria-label="Governance graph nodes">
          <TableHeader>
            <TableRow>
              <TableHeaderCell>Type</TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={sortField === 'label' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined}
                sortIcon={sortIcon('label')}
                onClick={() => toggleSort('label')}
                style={{ width: '100%' }}
              >
                Label
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'externalId' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('externalId')}
                onClick={() => toggleSort('externalId')}
              >
                External ID
              </TableHeaderCell>
              <TableHeaderCell
                sortable
                sortDirection={
                  sortField === 'validFrom' ? (sortDir === 'asc' ? 'ascending' : 'descending') : undefined
                }
                sortIcon={sortIcon('validFrom')}
                onClick={() => toggleSort('validFrom')}
              >
                Valid From
              </TableHeaderCell>
              <TableHeaderCell style={{ width: actionsColumnWidth(1) }}>Actions</TableHeaderCell>
            </TableRow>
          </TableHeader>
          <TableBody>
            {nodeItems.map((item) => (
              <TableRow key={item.id} onClick={() => handleNodeClick(item)} style={{ cursor: 'pointer' }}>
                <TableCell>
                  <Badge
                    appearance="filled"
                    style={{ backgroundColor: NODE_TYPE_COLORS[item.nodeType], color: tokens.colorNeutralForegroundStaticInverted }}
                  >
                    {item.nodeType}
                  </Badge>
                </TableCell>
                <TableCell>
                  <Body1 style={{ fontWeight: 500 }}>{item.label}</Body1>
                </TableCell>
                <TableCell>
                  <Caption1>{item.externalId}</Caption1>
                </TableCell>
                <TableCell>
                  <Caption1>{new Date(item.validFrom).toLocaleDateString()}</Caption1>
                </TableCell>
                <TableCell>
                  {/* Read-only list — chevron-only affordance opening the existing context drawer (no mutation, no role gate). */}
                  <Button
                    appearance="subtle"
                    icon={<ChevronRight24Regular />}
                    size="small"
                    aria-label={`View details for ${item.label}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      handleNodeClick(item);
                    }}
                  />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </ListLoadingOverlay>
    );
  }

  return (
    <div className={ps.page}>
      {/* Header */}
      <PageHeader title="Governance Graph" subtitle="Relationship map of policies, controls, assets, and evidence. The knowledge graph behind LeftShield's governance intelligence." icon={<ShareAndroidRegular />} iconColor={solutionTokens.brandSecondary} />

      {/* Stats Bar */}
      <div className={styles.statsGrid}>
        <div className={styles.statCard}>
          <Body1 className={styles.statValue}>{stats?.totalNodes ?? 0}</Body1>
          <Caption1>Total Nodes</Caption1>
        </div>
        <div className={styles.statCard}>
          <Body1 className={styles.statValue}>{stats?.totalEdges ?? 0}</Body1>
          <Caption1>Total Edges</Caption1>
        </div>
        {ALL_NODE_TYPES.filter(t => (stats?.nodeCountsByType[t] ?? 0) > 0).map(type => (
          <div key={type} className={styles.statCard}>
            <Body1 className={styles.statValue} style={{ color: NODE_TYPE_COLORS[type] }}>
              {stats?.nodeCountsByType[type] ?? 0}
            </Body1>
            <Caption1>{type}s</Caption1>
          </div>
        ))}
      </div>

      {/* Governance Gaps Panel */}
      {showGaps && (
        <Card>
          <CardHeader
            image={<WarningRegular style={{ fontSize: tokens.fontSizeBase600, color: semanticColors.warning }} />}
            header={<Title2>Governance Gaps</Title2>}
            description="Controls without evidence, policies without evaluations"
          />
          <div style={{ padding: tokens.spacingHorizontalL }}>
            <GapAnalysis nodes={gapNodeList?.items ?? []} context={context ?? null} />
          </div>
        </Card>
      )}

      {/* Node List */}
      <div className={styles.listSection}>
        <ListPageLayout
          toolbar={
            <>
              <ListToolbar
                secondaryActions={
                  <Button
                    appearance={showGaps ? 'primary' : 'outline'}
                    icon={<WarningRegular />}
                    onClick={() => setShowGaps(!showGaps)}
                  >
                    {showGaps ? 'Showing Gaps' : 'Show Governance Gaps'}
                  </Button>
                }
                search={state.search}
                onSearchChange={setSearch}
                searchPlaceholder="Search nodes by label, type, or ID…"
                onRefresh={refetchNodes}
                isRefreshing={nodesFetching}
                testIdPrefix="ekg-nodes"
              />
              <div className={styles.nodeTypeTabs}>
                <Caption1>
                  {totalCount} node{totalCount !== 1 ? 's' : ''}
                  {nodeTypeFilter ? ` of type ${nodeTypeFilter}` : ''}
                </Caption1>
                {nodesFetching && !nodesLoading && <Spinner size="tiny" data-testid="grid-loading" />}
                <TabList
                  selectedValue={selectedNodeType}
                  onTabSelect={(_, data) => handleNodeTypeSelect(data.value as string)}
                >
                  <Tab value="all">All</Tab>
                  {ALL_NODE_TYPES.map(type => (
                    <Tab key={type} value={type}>{type}</Tab>
                  ))}
                </TabList>
              </div>
            </>
          }
          footer={
            <Pager
              page={state.page}
              totalPages={totalPages}
              hasPreviousPage={hasPreviousPage}
              hasNextPage={hasNextPage}
              totalCount={totalCount}
              onPageChange={setPage}
              testId="ekg-nodes-pager"
            />
          }
        >
          {listBody}
        </ListPageLayout>
      </div>

      {/* Context Drawer */}
      <Drawer
        open={drawerOpen}
        onOpenChange={(_, { open }) => {
          if (!open) closeDrawer();
        }}
        position="end"
        size="medium"
      >
        <DrawerHeader>
          <DrawerHeaderTitle
            action={
              <Button
                appearance="subtle"
                icon={<DismissRegular />}
                onClick={closeDrawer}
              />
            }
          >
            {selectedNode?.label ?? 'Node Details'}
          </DrawerHeaderTitle>
        </DrawerHeader>
        <DrawerBody>
          {selectedNode && (
            <ContextPanel node={selectedNode} context={context ?? null} />
          )}
        </DrawerBody>
      </Drawer>
    </div>
  );
};

// ─── Gap Analysis Component ─────────────────────────────────

interface GapAnalysisProps {
  nodes: EkgNode[];
  context: EkgContext | null;
}

const GapAnalysis: React.FC<GapAnalysisProps> = ({ nodes }) => {
  const styles = useStyles();

  const controlNodes = nodes.filter(n => n.nodeType === 'Control');
  const policyNodes = nodes.filter(n => n.nodeType === 'Policy');

  return (
    <div className={styles.drawerContent}>
      <div className={styles.neighborSection}>
        <Body1 style={{ fontWeight: 600 }}>
          <WarningRegular style={{ marginRight: tokens.spacingHorizontalS, color: semanticColors.warning }} />
          Controls Without Evidence ({controlNodes.length > 0 ? 'review needed' : 'no controls imported yet'})
        </Body1>
        {controlNodes.length > 0 ? (
          controlNodes.slice(0, 10).map(node => (
            <div key={node.id} className={styles.neighborItem}>
              <Badge appearance="filled" size="small" style={{ backgroundColor: NODE_TYPE_COLORS.Control, color: tokens.colorNeutralForegroundStaticInverted }}>Control</Badge>
              <Body1 style={{ fontSize: tokens.fontSizeBase200 }}>{node.label}</Body1>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Verify evidence linkage</Caption1>
            </div>
          ))
        ) : (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>No control nodes in graph yet. Controls are created when frameworks are mapped.</Caption1>
        )}
      </div>
      <Divider />
      <div className={styles.neighborSection}>
        <Body1 style={{ fontWeight: 600 }}>
          Policies Without Evaluations ({policyNodes.length > 0 ? 'review needed' : 'no policies configured yet'})
        </Body1>
        {policyNodes.length > 0 ? (
          policyNodes.slice(0, 10).map(node => (
            <div key={node.id} className={styles.neighborItem}>
              <Badge appearance="filled" size="small" style={{ backgroundColor: NODE_TYPE_COLORS.Policy, color: tokens.colorNeutralForegroundStaticInverted }}>Policy</Badge>
              <Body1 style={{ fontSize: tokens.fontSizeBase200 }}>{node.label}</Body1>
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>No recent evaluation</Caption1>
            </div>
          ))
        ) : (
          <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>No policy nodes in graph yet. Connect repositories to begin.</Caption1>
        )}
      </div>
    </div>
  );
};

// ─── Context Panel (Drawer Content) ─────────────────────────

interface ContextPanelProps {
  node: EkgNode;
  context: EkgContext | null;
}

const ContextPanel: React.FC<ContextPanelProps> = ({ node, context }) => {
  const styles = useStyles();
  const [activeTab, setActiveTab] = useState<string>('overview');

  const outbound = useMemo(
    () => context?.edges.filter(e => e.sourceNodeId === node.id) ?? [],
    [context, node.id],
  );

  const inbound = useMemo(
    () => context?.edges.filter(e => e.targetNodeId === node.id) ?? [],
    [context, node.id],
  );

  // Impact propagation: find all related nodes through edges
  const relatedNodes = useMemo(() => {
    if (!context) return [];
    const nodeIds = new Set<number>();
    context.edges.forEach(e => {
      if (e.sourceNodeId !== node.id) nodeIds.add(e.sourceNodeId);
      if (e.targetNodeId !== node.id) nodeIds.add(e.targetNodeId);
    });
    return context.nodes.filter(n => nodeIds.has(n.id));
  }, [context, node.id]);

  // "Why is this failing?" analysis for Control nodes
  const failingAnalysis = useMemo(() => {
    if (node.nodeType !== 'Control' || !context) return null;
    const policies = inbound.filter(e => e.edgeType === 'PolicyImplementsControl');
    const evidence = inbound.filter(e => e.edgeType === 'EvidenceSupportsControl');
    const findings = inbound.filter(e => e.edgeType === 'FindingViolatesControl');
    const frameworks = outbound.filter(e => e.edgeType === 'ControlSatisfiesRequirement');
    return { policies, evidence, findings, frameworks };
  }, [node, context, inbound, outbound]);

  return (
    <div className={styles.drawerContent}>
      <TabList selectedValue={activeTab} onTabSelect={(_, d) => setActiveTab(d.value as string)}>
        <Tab value="overview">Overview</Tab>
        <Tab value="relationships">Relationships</Tab>
        {node.nodeType === 'Control' && <Tab value="diagnosis">Why Failing?</Tab>}
        <Tab value="impact">Impact</Tab>
      </TabList>

      {activeTab === 'overview' && (
        <>
          {/* Node metadata */}
          <div className={styles.neighborSection}>
            <Body1 style={{ fontWeight: 600 }}>Details</Body1>
            <div className={styles.neighborItem}>
              <Caption1 style={{ minWidth: 80, fontWeight: 600 }}>Type</Caption1>
              <Badge
                appearance="filled"
                style={{ backgroundColor: NODE_TYPE_COLORS[node.nodeType], color: tokens.colorNeutralForegroundStaticInverted }}
              >
                {node.nodeType}
              </Badge>
            </div>
            <div className={styles.neighborItem}>
              <Caption1 style={{ minWidth: 80, fontWeight: 600 }}>External ID</Caption1>
              <Caption1>{node.externalId}</Caption1>
            </div>
            <div className={styles.neighborItem}>
              <Caption1 style={{ minWidth: 80, fontWeight: 600 }}>Valid From</Caption1>
              <Caption1>{new Date(node.validFrom).toLocaleString()}</Caption1>
            </div>
          </div>
        </>
      )}

      {activeTab === 'relationships' && (
        <>
          {/* Outbound edges */}
          <div className={styles.neighborSection}>
            <Body1 style={{ fontWeight: 600 }}>
              Outbound Relationships ({outbound.length})
            </Body1>
            {outbound.length > 0 ? (
              outbound.map((edge) => (
                <EdgeItem key={edge.id} edge={edge} direction="outbound" />
              ))
            ) : (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                No outbound relationships
              </Caption1>
            )}
          </div>

          {/* Inbound edges */}
          <div className={styles.neighborSection}>
            <Body1 style={{ fontWeight: 600 }}>
              Inbound Relationships ({inbound.length})
            </Body1>
            {inbound.length > 0 ? (
              inbound.map((edge) => (
                <EdgeItem key={edge.id} edge={edge} direction="inbound" />
              ))
            ) : (
              <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>
                No inbound relationships
              </Caption1>
            )}
          </div>
        </>
      )}

      {activeTab === 'diagnosis' && failingAnalysis && (
        <div className={styles.neighborSection}>
          <Body1 style={{ fontWeight: 600 }}>
            <QuestionCircleRegular style={{ marginRight: tokens.spacingHorizontalS }} />
            Why is "{node.label}" failing?
          </Body1>
          <Divider />

          <Body1 style={{ fontWeight: 600 }}>Mapped Policies ({failingAnalysis.policies.length})</Body1>
          {failingAnalysis.policies.length > 0
            ? failingAnalysis.policies.map(e => <EdgeItem key={e.id} edge={e} direction="inbound" />)
            : <Caption1 style={{ color: semanticColors.warning }}>No policies implement this control — enforcement gap</Caption1>
          }

          <Divider />
          <Body1 style={{ fontWeight: 600 }}>Evidence ({failingAnalysis.evidence.length})</Body1>
          {failingAnalysis.evidence.length > 0
            ? failingAnalysis.evidence.map(e => <EdgeItem key={e.id} edge={e} direction="inbound" />)
            : <Caption1 style={{ color: semanticColors.error }}>No evidence linked — audit risk</Caption1>
          }

          <Divider />
          <Body1 style={{ fontWeight: 600 }}>Active Findings ({failingAnalysis.findings.length})</Body1>
          {failingAnalysis.findings.length > 0
            ? failingAnalysis.findings.map(e => <EdgeItem key={e.id} edge={e} direction="inbound" />)
            : <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>No active violations</Caption1>
          }

          <Divider />
          <Body1 style={{ fontWeight: 600 }}>Impacted Frameworks ({failingAnalysis.frameworks.length})</Body1>
          {failingAnalysis.frameworks.length > 0
            ? failingAnalysis.frameworks.map(e => <EdgeItem key={e.id} edge={e} direction="outbound" />)
            : <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>Not mapped to any framework</Caption1>
          }
        </div>
      )}

      {activeTab === 'impact' && (
        <div className={styles.neighborSection}>
          <Body1 style={{ fontWeight: 600 }}>Impact Propagation ({relatedNodes.length} connected nodes)</Body1>
          {relatedNodes.length > 0 ? (
            relatedNodes.map(n => (
              <div key={n.id} className={styles.neighborItem}>
                <Badge appearance="filled" size="small" style={{ backgroundColor: NODE_TYPE_COLORS[n.nodeType], color: tokens.colorNeutralForegroundStaticInverted }}>{n.nodeType}</Badge>
                <Body1 style={{ fontSize: tokens.fontSizeBase200 }}>{n.label}</Body1>
              </div>
            ))
          ) : (
            <Caption1 style={{ color: tokens.colorNeutralForeground3 }}>No connected nodes found</Caption1>
          )}
        </div>
      )}
    </div>
  );
};

// ─── Edge Item ──────────────────────────────────────────────

interface EdgeItemProps {
  edge: EkgEdge;
  direction: 'outbound' | 'inbound';
}

const EdgeItem: React.FC<EdgeItemProps> = ({ edge, direction }) => {
  const styles = useStyles();
  const label = direction === 'outbound' ? edge.targetLabel : edge.sourceLabel;
  const nodeType = direction === 'outbound' ? edge.targetNodeType : edge.sourceNodeType;

  return (
    <div className={styles.neighborItem}>
      <Badge appearance="outline" size="small">
        {EDGE_TYPE_LABELS[edge.edgeType] ?? edge.edgeType}
      </Badge>
      {nodeType && (
        <Badge
          appearance="filled"
          size="small"
          style={{ backgroundColor: NODE_TYPE_COLORS[nodeType], color: tokens.colorNeutralForegroundStaticInverted }}
        >
          {nodeType}
        </Badge>
      )}
      <Body1 style={{ fontSize: tokens.fontSizeBase200 }}>{label ?? '—'}</Body1>
    </div>
  );
};
